// Punto de encuentro: capacidad (PE-8), lista de condiciones con eliminatorias y matriz
// probabilidad × consecuencia del plan de referencia: aceptable hasta "tolerable"; "moderado" o peor
// exige una acción obligatoria en el plan de acción (PE-24, Kevin 26/10/2026).

import {
  CONDICIONES_PUNTO_ENCUENTRO, Consecuencia, ETIQUETA_RIESGO_PC, FUENTE_CONDICIONES, FUENTE_MATRIZ_PC,
  MATRIZ_PROBABILIDAD_CONSECUENCIA, ORDEN_RIESGO_PC, Probabilidad, RiesgoPC,
} from './pe-constantes-plan-referencia';
import { Traza } from './pe-tipos';
import { numEc } from './pe-unidades';

const METODO = 'punto_encuentro';

export function riesgoPeligro(p: Probabilidad, c: Consecuencia): RiesgoPC {
  return MATRIZ_PROBABILIDAD_CONSECUENCIA[p][c];
}

export const requiereAccion = (r: RiesgoPC): boolean => ORDEN_RIESGO_PC.indexOf(r) >= ORDEN_RIESGO_PC.indexOf('MO');

type ClaveCondicion = typeof CONDICIONES_PUNTO_ENCUENTRO[number]['clave'];

export interface EntradaPuntoEncuentro {
  ocupantes: number;
  m2PorPersona?: number;          // definido por el responsable (PE-8)
  areaDisponibleM2?: number;
  distanciaM?: number;
  distanciaMinimaM?: number;      // definida por el responsable (PE-8)
  condiciones: Partial<Record<ClaveCondicion, boolean>>;
  peligros: { peligro: string; probabilidad: Probabilidad; consecuencia: Consecuencia }[];
}

export interface AccionObligatoria { origen: string; descripcion: string; pe: string }

export function evaluarPuntoEncuentro(e: EntradaPuntoEncuentro) {
  const trazas: Traza[] = [];
  const acciones: AccionObligatoria[] = [];

  // Capacidad = ocupantes × m² por persona.
  const areaRequeridaM2 = e.m2PorPersona !== undefined ? e.ocupantes * e.m2PorPersona : null;
  trazas.push({
    metodo: METODO, norma: FUENTE_CONDICIONES.norma, articulo_o_tabla: 'Capacidad', umbral: areaRequeridaM2,
    valor: e.areaDisponibleM2 ?? null, unidad: 'm²',
    estado: areaRequeridaM2 === null || e.areaDisponibleM2 === undefined ? 'por_validar'
      : e.areaDisponibleM2 >= areaRequeridaM2 ? 'cumple' : 'no_cumple',
    fuente_doc: FUENTE_CONDICIONES.fuente_doc,
    nota: areaRequeridaM2 === null ? 'Falta el m² por persona que define el responsable'
      : `${e.ocupantes} personas × ${numEc(e.m2PorPersona as number)} m²/persona (criterio técnico del responsable)`,
  });

  if (e.distanciaMinimaM !== undefined && e.distanciaM !== undefined) {
    trazas.push({
      metodo: METODO, norma: FUENTE_CONDICIONES.norma, articulo_o_tabla: 'Distancia a la edificación',
      umbral: e.distanciaMinimaM, valor: e.distanciaM, unidad: 'm',
      estado: e.distanciaM >= e.distanciaMinimaM ? 'cumple' : 'no_cumple', fuente_doc: FUENTE_CONDICIONES.fuente_doc,
      nota: 'Distancia mínima definida por el responsable',
    });
  }

  let descartado = false;
  for (const c of CONDICIONES_PUNTO_ENCUENTRO) {
    const v = e.condiciones[c.clave];
    if (v === undefined) continue;
    if (!v && c.eliminatoria) descartado = true;
    trazas.push({
      metodo: METODO, norma: FUENTE_CONDICIONES.norma, articulo_o_tabla: c.etiqueta, umbral: 'sí', valor: v ? 'sí' : 'no',
      unidad: '—', estado: v ? 'cumple' : 'no_cumple', fuente_doc: FUENTE_CONDICIONES.fuente_doc,
      nota: c.eliminatoria ? 'Condición eliminatoria' : undefined,
    });
  }

  const evaluacion = e.peligros.map(p => {
    const riesgo = riesgoPeligro(p.probabilidad, p.consecuencia);
    const accion = requiereAccion(riesgo);
    if (accion) {
      acciones.push({ origen: p.peligro, descripcion: `Controlar "${p.peligro}" (riesgo ${ETIQUETA_RIESGO_PC[riesgo]})`, pe: 'PE-24' });
    }
    trazas.push({
      metodo: METODO, norma: FUENTE_MATRIZ_PC.norma, articulo_o_tabla: 'Probabilidad × consecuencia', umbral: 'hasta tolerable',
      valor: riesgo, unidad: '—', estado: accion ? 'no_cumple' : 'cumple', fuente_doc: FUENTE_MATRIZ_PC.fuente_doc,
      nota: `${p.peligro}: ${p.probabilidad} × ${p.consecuencia} = ${ETIQUETA_RIESGO_PC[riesgo]}${accion ? '; genera acción obligatoria en el plan de acción' : ''}`,
    });
    return { ...p, riesgo, requiereAccion: accion };
  });

  return { areaRequeridaM2, descartado, evaluacion, acciones, trazas };
}
